import React, { useEffect } from "react";
import { bool, number, oneOfType, shape, string } from "prop-types";

import { handleSetPassword } from "../../../utilities";
import HeaderNavButton from "../../HeaderNavButton";
import { List, ListItem } from "../../HeaderNavDropdownList";
import { useMainNavDropdown } from "../../MainNav/hooks";
import * as S from "./styled";

const handleUserLogout = () => {
  if (
    window?.SE?.cookies?.getCookie &&
    window?.performance?.navigation?.type !== undefined
  ) {
    const token = window.SE.cookies.getCookie("user_auth_token");

    if (!token && window.performance.navigation.type === 2) {
      window.location.reload(true);
    }
  }
};

const AccountToolsDropdown = ({
  belongs_to_rmc,
  current_user: { email, login, name },
  is_agent_or_small_landlord,
  is_brokerage_pi,
  is_free_pass,
  is_lists,
  is_manager,
  is_notification_preferences,
  is_pro,
  masquerader,
  owns_billing_account
}) => {
  const {
    dropdownRef,
    isActive,
    onClick,
    onMouseEnter,
    onMouseLeave
  } = useMainNavDropdown();
  const hasBillingHistoryLink = !!belongs_to_rmc;
  const hasBillingAccountLink =
    !belongs_to_rmc && (owns_billing_account || is_brokerage_pi);

  const ProfileLink = (
    <List as="div">
      <ListItem as="div" link="/my/profile" title="Profile" />
    </List>
  );
  const AccountProfileLink = (
    <ListItem link="/my/account_profile" title="Profile" />
  );
  const PasswordSetLink = is_free_pass ? (
    <ListItem
      data-gtm-header-menu="Your Account"
      link="#"
      onClick={handleSetPassword}
      title="Set a Password"
    />
  ) : null;
  const FoldersLink = !is_lists ? (
    <ListItem data-gtm-header-menu="Folders" link="/folders" title="Folders" />
  ) : null;
  const SettingsLink = <ListItem link="/my/settings" title="Settings" />;
  const NotificationPreferencesLink = is_notification_preferences ? (
    <ListItem link="/nyc/user/notification_preferences" title="Notifications" />
  ) : (
    <ListItem link="/nyc/user/email_preferences" title="Email Preferences" />
  );
  const BillingHistoryLink = hasBillingHistoryLink ? (
    <ListItem
      link="/manage/property_management/dashboard/billing_history?site=nyc"
      title="Billing History"
    />
  ) : null;
  const BillingAccountLink = hasBillingAccountLink ? (
    <ListItem link="/my/billing_featured/index" title="Billing Account" />
  ) : null;
  const LogoutLink = (
    <List as="div">
      <ListItem
        as="div"
        data-gtm-header-menu="Your Account"
        itemName="logout"
        link="/user/logout"
        title="Sign Out"
      />
    </List>
  );

  useEffect(() => {
    handleUserLogout();
  }, []);

  return (
    <>
      <HeaderNavButton
        aria-expanded={isActive}
        aria-haspopup="true"
        aria-label="Account"
        data-type="Your Account"
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        Account &nbsp;
        <S.CaretIcon
          data-user-agent={`${is_agent_or_small_landlord || is_pro || false}`}
          data-user-email={email}
          data-user-login={login}
          data-user-masquerader={masquerader || null}
          data-user-name={name}
        />
      </HeaderNavButton>
      <div ref={dropdownRef}>
        <S.StyledMenuPopup
          isActive={isActive}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          role="tooltip"
        >
          {is_agent_or_small_landlord || is_manager ? (
            <>
              {ProfileLink}
              <List>
                {PasswordSetLink}
                {SettingsLink}
                {NotificationPreferencesLink}
              </List>
              {hasBillingHistoryLink ? (
                <List as="div">
                  <ListItem
                    as="div"
                    link="/manage/property_management/dashboard/billing_history?site=nyc"
                    title="Billing History"
                  />
                </List>
              ) : null}
              {hasBillingAccountLink ? (
                <List as="div">
                  <ListItem
                    as="div"
                    link="/my/billing_featured/index"
                    title="Billing Account"
                  />
                </List>
              ) : null}
            </>
          ) : (
            <List>
              {PasswordSetLink}
              {FoldersLink}
              {BillingHistoryLink}
              {BillingAccountLink}
              {AccountProfileLink}
              {SettingsLink}
              {NotificationPreferencesLink}
            </List>
          )}
          {LogoutLink}
        </S.StyledMenuPopup>
      </div>
    </>
  );
};

AccountToolsDropdown.propTypes = {
  belongs_to_rmc: bool,
  current_user: shape({
    email: string,
    login: string,
    name: string
  }),
  is_agent_or_small_landlord: bool,
  is_brokerage_pi: bool,
  is_free_pass: bool,
  is_lists: bool,
  is_manager: bool,
  is_notification_preferences: bool,
  is_pro: bool,
  masquerader: oneOfType([number, string]),
  owns_billing_account: bool
};

AccountToolsDropdown.defaultProps = {
  belongs_to_rmc: false,
  current_user: {},
  is_agent_or_small_landlord: false,
  is_brokerage_pi: false,
  is_free_pass: false,
  is_lists: false,
  is_manager: false,
  is_notification_preferences: false,
  is_pro: false,
  masquerader: null,
  owns_billing_account: false
};

export default AccountToolsDropdown;
